import { useState, useEffect, useRef, Activity } from 'react';

// types
import {
  SelectItemProps,
  SelectListPosition,
  SelectProps,
  SelectSize,
  SelectType,
} from './types';

// components
import OptionsList from './options-list';
import SelectedValue from './selected-value';
import IconArrowDown from '../icons/down';
import ClickOutsideDetector from '../autocomplete/click-outside-detector';
import usePrevious from '../../hooks/use-previous';
import IconClose from '../icons/close';
import IconInfo from '../icons/info';

import styles from './select.module.css';

export default function Select({
  mode = SelectType.SINGLE,
  disabled = false,
  list = [],
  onChange,
  placeholder = '',
  title = '',
  name = '',
  className = '',
  isOptionsListVisible = false,
  hideSelectedItems = false,
  value,
  size = SelectSize.MEDIUM,
  borderless = false,
  selectWrapperClassName = '',
  selectListPosition = SelectListPosition.BOTTOM,
  fullWidth = false,
  isLoading = false,
  emptyContentButton,
  error,
  tabIndex,
  yearSelector,
  withClearButton,
  withButtonInTitle,
  onTitleButtonClick,
  titleButtonText,
  hintText,
  withoutSelectedValue,
}: SelectProps): React.JSX.Element {
  const ulRef = useRef<HTMLUListElement | null>(null);
  const [selectValues, setSelectedValues] = useState<string[]>([]);
  const [toggledOn, toggle] = useState(false);
  const [selectedNode, setSelectedNode] = useState(0);
  const [focused, setFocused] = useState(false);

  const prevOpen = usePrevious(toggledOn);

  const handleSelectValue = (item: SelectItemProps) => {
    let nextSelectedValues = ([] as string[]).concat(selectValues);
    const itemIsSelected = selectValues.some(
      (selectedItem) => selectedItem.toString() === item.value.toString(),
    );

    if (mode !== SelectType.MULTIPLE) {
      nextSelectedValues = [item.value.toString()];
      toggle(false);
    }

    if (mode === SelectType.MULTIPLE) {
      if (itemIsSelected) {
        nextSelectedValues = nextSelectedValues.filter(
          (selectedItem) => selectedItem !== item.value,
        );
      } else {
        nextSelectedValues.push(item.value.toString());
      }
    }

    if (!withoutSelectedValue) {
      setSelectedValues(nextSelectedValues);
    }

    if (onChange) {
      onChange({
        target: {
          name,
          value: mode === SelectType.MULTIPLE ? nextSelectedValues : item.value,
        },
      });
    }
  };

  const removeItem = (item: string | number) => {
    const nextSelectedValues = selectValues.filter(
      (selectedItem) => selectedItem !== item,
    );

    setSelectedValues(nextSelectedValues);

    if (onChange) {
      onChange({
        target: {
          name,
          value: nextSelectedValues,
        },
      });
    }
  };

  const onScroll = (e: any) => {
    const nodes = ulRef?.current?.querySelectorAll('li');

    if (nodes) {
      const firstNode = nodes[0];
      const elHeight = firstNode.clientHeight;

      const node = nodes[Math.round(e.target.scrollTop / elHeight)];

      if (node) {
        // @ts-ignore
        const currentIndex = [].indexOf.call(nodes, node);

        if (currentIndex && currentIndex !== undefined && currentIndex !== -1) {
          const prevNode = nodes[selectedNode];
          const currentNode = nodes[currentIndex];

          prevNode?.classList?.remove(styles.hovered);
          currentNode?.classList.add(styles.hovered);

          setSelectedNode(currentIndex);
        }
      }
    }
  };

  const onSelectNode = ({
    node,
    selected,
  }: {
    node: HTMLLIElement;
    selected: number;
  }) => {
    if (node) {
      node.classList.add(styles.hovered);

      const elHeight = node.clientHeight;
      const scrollTop = ulRef?.current?.scrollTop;

      if (scrollTop !== undefined && ulRef?.current) {
        const ulHeight = ulRef?.current?.clientHeight;
        const viewport = scrollTop + ulHeight;

        const elOffset = elHeight * selected;

        if (elOffset < scrollTop || elOffset + elHeight > viewport) {
          if (selectedNode !== 1) {
            ulRef?.current.scrollTo(0, elOffset);
          } else {
            ulRef?.current.scrollTo(0, 0);
          }
        }
      }
    }
  };

  const onEnter = () => {
    list.forEach((item, index) => {
      if (index === selectedNode) {
        handleSelectValue(item);
      }
    });
  };

  const onUlKeydown = (e: { code: string; preventDefault: () => void }) => {
    const nodes = ulRef?.current?.querySelectorAll('li');
    let nextNode;

    if (nodes) {
      if (e.code === 'ArrowDown' && nodes[selectedNode + 1]) {
        setSelectedNode(selectedNode + 1);
        nextNode = selectedNode + 1;
        onSelectNode({
          node: nodes[selectedNode + 1],
          selected: selectedNode + 1,
        });
      }

      if (e.code === 'ArrowUp' && selectedNode !== 0) {
        nextNode = selectedNode - 1;

        setSelectedNode(selectedNode - 1);
        onSelectNode({
          node: nodes[selectedNode - 1],
          selected: selectedNode - 1,
        });
      }

      if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
        const prevLi = nodes[selectedNode];

        if (prevLi && nodes[nextNode as number]) {
          prevLi.classList.remove(styles.hovered);
        }
      }

      e.preventDefault();
    }
  };

  const onClear = () => {
    setSelectedValues([]);
    if (onChange) {
      onChange({
        target: {
          name,
          value: '',
        },
      });
    }
  };

  useEffect(() => {
    toggle(isOptionsListVisible);
  }, [isOptionsListVisible]);

  useEffect(() => {
    if (ulRef && ulRef.current && toggledOn) {
      const nodes = ulRef.current.querySelectorAll('li');

      if (nodes[0]) {
        const firstNode = nodes[0];

        if (firstNode) {
          firstNode.classList.add(styles.hovered);
        }
      }
    }
  }, [ulRef, toggledOn]);

  useEffect(() => {
    if (!toggledOn && prevOpen) {
      setSelectedNode(0);

      ulRef?.current?.removeEventListener('keydown', () => {});
    }
  }, [ulRef, toggledOn, prevOpen]);

  useEffect(() => {
    if (ulRef && ulRef.current && toggledOn && yearSelector) {
      setTimeout(() => {
        if (ulRef && ulRef.current) {
          const nodes = ulRef.current.querySelectorAll('li');

          if (nodes) {
            let currentNode: HTMLLIElement | undefined;
            let currentNodeIndex: number | undefined;

            nodes.forEach((item, index) => {
              if (
                item.innerText &&
                selectValues[0] &&
                Number(item.innerText) === Number(selectValues[0])
              ) {
                currentNode = item;
                currentNodeIndex = index;
              }
            });

            if (currentNode) {
              const positionTop = currentNode.offsetTop;

              if (positionTop) {
                ulRef.current.scrollTop =
                  positionTop - currentNode.offsetHeight * 3.5;

                setSelectedNode(currentNodeIndex as number);
              }
            }
          }
        }
      }, 1);
    }
  }, [ulRef, toggledOn, yearSelector, selectValues]);

  useEffect(() => {
    if (value) {
      let nextValues = [value.toString()];

      if (mode === SelectType.MULTIPLE) {
        nextValues = value as string[];
      }

      setSelectedValues(nextValues);
    } else if (!value && selectValues.length > 0) {
      setSelectedValues([]);
    }
  }, [value, mode]);

  return (
    <ClickOutsideDetector
      setOptionsListVisible={toggle}
      className={className}
      onMouseEnter={() => setFocused(true)}
      onMouseLeave={() => setFocused(false)}
    >
      <div
        className={`${styles.selectWrapper} ${
          disabled ? styles.disabled : ''
        } ${className} ${size === SelectSize.SMALL ? styles.small : ''}`}
        onKeyDown={(e) => {
          if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
            onUlKeydown(e);
          }

          if (e.code === 'Enter') {
            onEnter();
          }
        }}
        role="button"
        tabIndex={-1}
      >
        {title && (
          <div className={styles.selectTitle}>
            {title}
            {withButtonInTitle && (
              <button
                type="button"
                className={styles.selectTitleButton}
                onClick={onTitleButtonClick}
              >
                {titleButtonText}
              </button>
            )}
            {hintText && (
              <div className={styles.titleHint} data-text={hintText}>
                <IconInfo width={16} height={16} />
              </div>
            )}
          </div>
        )}
        <div>
          <div
            className={`${styles.select} ${toggledOn ? styles.toggled : ''} ${
              borderless ? styles.selectBorderless : ''
            } ${selectWrapperClassName} ${fullWidth ? styles.fullWidth : ''} ${
              mode === SelectType.MULTIPLE ? styles.selectMultiple : ''
            }
            ${error ? styles.error : ''}`}
          >
            {placeholder && !selectValues.length && !hideSelectedItems && (
              <div className={styles.placeholder}>{placeholder}</div>
            )}
            {selectValues.length !== list.length && (
              <button
                type="button"
                className={styles.toggleButton}
                aria-label="Toggle"
                onClick={() =>
                  toggle(mode === SelectType.MULTIPLE ? true : !toggledOn)
                }
                onFocus={() => {
                  if (!focused) {
                    toggle(true);
                  }
                }}
                onKeyDown={(evt) => {
                  if (evt.key === 'Tab') {
                    setFocused(false);
                    toggle(false);
                  }
                }}
                tabIndex={tabIndex}
              />
            )}
            <IconArrowDown
              className={`${styles.arrow} ${toggledOn ? styles.rotate : ''}`}
            />
            {Boolean(selectValues.length) && !hideSelectedItems && (
              <SelectedValue
                selectValues={selectValues}
                mode={mode}
                list={list}
                removeItem={removeItem}
                size={size}
                disabled={disabled}
              />
            )}
            {Boolean(value) && withClearButton && !disabled && (
              <button
                type="button"
                className={`${styles.iconWrapper} ${styles.iconWrapperClear}`}
                onClick={onClear}
                tabIndex={-1}
                aria-label="Close"
              >
                <IconClose className={styles.icon} />
              </button>
            )}
            <Activity mode={toggledOn ? 'visible' : 'hidden'}>
              <OptionsList
                list={list?.filter((item) => !item.hidden)}
                isLoading={isLoading}
                selectValues={selectValues}
                handleSelectValue={handleSelectValue}
                selectListPosition={selectListPosition}
                emptyContentButton={emptyContentButton}
                ulRef={ulRef}
                onScroll={onScroll}
                mode={mode}
              />
            </Activity>
          </div>
        </div>
        {error && <div className={styles.errorMessage}>{error}</div>}
      </div>
    </ClickOutsideDetector>
  );
}
